import { Component } from "react";
import ObjectUtils, { coalesce } from "../../../../utils/ObjectUtils";
import { cleanText } from "../../../../utils/QuestionUtils";

import { faEdit, faGripVertical, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { injectIntl } from "react-intl";
import TrashButton from "../../../common/TrashButton";
import QuestionValidation from "./QuestionValidation";
import CheckboxRenderer from "./renderer/CheckboxRenderer";
import DateRenderer from "./renderer/DateRenderer";
import RadioRenderer from "./renderer/RadioRenderer";
import SimpleRenderer from "./renderer/SimpleRenderer";
import TextRenderer from "./renderer/TextRenderer";

const { findByTemplate, getDeepProperty } = ObjectUtils;

/**
 * Single row of the table question component for survey-view.
 */
class TableRow extends Component {
// ✅ Event handlers defined as class fields – no need for .bind()
handleDragStart = (rowAnswers, row, event) => {
  event.stopPropagation();
  event.dataTransfer.setData("text", row.id);
};

handleDragOver = (event) => {
  event.preventDefault();
};

handleDrop = (event) => {
  const rowId = event.dataTransfer.getData("text");
  const { reorderQuestionRow, question, row } = this.props;
  reorderQuestionRow(question.name, rowId, row.id);
  event.stopPropagation();
  event.preventDefault();
};

handleEdit = (event) => {
  event.stopPropagation();
  const { index, monitoringEditOnly, editRow } = this.props;
  editRow(index, monitoringEditOnly);
};

handleDelete = (event) => {
  event.stopPropagation();
  const { question, row, deleteRow } = this.props;
  deleteRow(question, row.id);
};


  render() {
    const {
      editRow,
      rowAnswers,
      skipFirst,
      row,
      index,
      question,
      disabled,
      monitoringEditOnly,
      textExpanded,
      intl
    } = this.props;

    let result = [];

    const isDynamicTable = question.type === "dynamicTable";
    if (isDynamicTable) {
      const columns = question.columns || [];
      for (let i = 0, len = columns.length; i < len; i++) {
        const column = columns[i];
        const columnClass = ObjectUtils.toKebabCase(column.type);

        const colors = {
          "Option-0": "rgba(51, 189, 30, 0.4)",
          "Option-2": "rgba(246, 255, 0, 0.4)",
          "Option-3": "rgba(252, 0, 0, 0.4)"
        };

        const cssClass = `${textExpanded || columnClass.indexOf("text") < 0 ? "" : "text-truncate"} ${columnClass}`;

        if (column.visible !== false) {
          if (textExpanded && column.name === "Spalten-2") {
            const tmp = rowAnswers.find((r) => r.colLabel === "Spalten-2");
            const col = colors[tmp?.name];
            result.push(
              <td key={`C${i}`} className={cssClass} style={{ backgroundColor: col ? col : "transparent" }}>
                <SimpleRenderer question={question} collection={rowAnswers} row={row} column={column} intl={intl}/>
              </td>
            );
          } else {
            result.push(
              <td key={`C${i}`} className={cssClass}>
                <SimpleRenderer question={question} collection={rowAnswers} row={row} column={column} intl={intl}/>
              </td>
            );
          }
        }
      }

      result.push(
        <td key={`dynamicTableRow${index}`} className="bg-light actions shrink">
          <div className="d-inline-flex gap-1">
              <Button
                variant="link"
                disabled={disabled && !monitoringEditOnly}
                size="sm"
                title={intl.formatMessage({ id: "app.common.action.edit" })}
                onClick={this.handleEdit}
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
              <TrashButton
                onClick={this.handleDelete}
                disabled={disabled}
              />
              <Button
                variant="link"
                draggable
                disabled={disabled}
                size="sm"
                title={intl.formatMessage({ id: "app.common.action.move" })}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onDragStart={(e) => this.handleDragStart(rowAnswers, row, e)}
                style={{ cursor: "grab" }}
              >
                <FontAwesomeIcon icon={faGripVertical} />
              </Button>
            </div>
        </td>
      );
    } else {
      result = result.concat(this.renderColumns());
    }

    return (
      <tr
        key={row.uid || row.id || index}
        onClick={(!disabled || monitoringEditOnly) && isDynamicTable ? () => editRow(index) : undefined}
        onDragOver={this.handleDragOver}
        onDrop={this.handleDrop}
      >
        {isDynamicTable && <td className="shrink bg-light text-center">{index + 1}</td>}
        {!skipFirst && (
          <td key={index}>
            {row.required ? "* " : " "}
            {row.text}
            {row.infoText && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip" className="survey-tooltip">
                    {row.infoText}
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faInfoCircle} />
              </OverlayTrigger>
            )}
          </td>
        )}
        {result}
      </tr>
    );
  }

  renderColumns() {
    const {
      question,
      disabled,
      row,
      rowAnswers,
      validation,
      addQuestionAnswer,
      setQuestionAnswer,
      removeQuestionAnswer
    } = this.props;

    const result = [];

    const columns = question.columns || [];
    for (let i = 0, len = columns.length; i < len; i++) {
      const column = columns[i];
      const columnAnswer = rowAnswers.find(
        (answer) => answer.colLabel === column.name && cleanText(answer.value).length
      );
      const incompleteClass = (column.required || row.required) && !columnAnswer ? "required-cell-incomplete" : "";

      const qValidators = findByTemplate(coalesce(getDeepProperty(question, "validators"), []), {
        columnName: column.name
      });
      const qValidations = findByTemplate(coalesce(validation, []), {
        columnName: column.name,
        rowName: row.name
      });
      const validator = getDeepProperty(qValidators, 0);
      const qValidation = getDeepProperty(qValidations, 0);
      switch (column.type) {
        case "Text":
          result.push(
            <td key={`C${i}`} className={incompleteClass}>
              <TextRenderer
                row={row}
                column={column}
                question={question}
                disabled={disabled}
                collection={rowAnswers}
                setQuestionAnswer={setQuestionAnswer}
                showLengthValidation
              />
            </td>
          );
          break;

        case "Number":
          result.push(
            <td key={`C${i}`} className={`number ${incompleteClass}`}>
              <TextRenderer
                row={row}
                column={column}
                question={question}
                disabled={disabled}
                collection={rowAnswers}
                setQuestionAnswer={setQuestionAnswer}
              />

              <QuestionValidation
                showOnlyError
                validation={qValidation}
                validator={validator}
                questionType={column.type.toLowerCase()}
              />
            </td>
          );
          break;

        case "Date":
          result.push(
            <td key={`C${i}`} className={`date ${incompleteClass}`}>
              <DateRenderer
                row={row}
                column={column}
                question={question}
                disabled={disabled}
                collection={rowAnswers}
                setQuestionAnswer={setQuestionAnswer}
              />
            </td>
          );
          break;

        case "Radio":
        case "Dropdown":
        case "predefinedList":
          result.push(
            <td key={`C${i}`} className={incompleteClass}>
              <RadioRenderer
                row={row}
                column={column}
                question={question}
                disabled={disabled}
                collection={rowAnswers}
                setQuestionAnswer={setQuestionAnswer}
                removeQuestionAnswer={removeQuestionAnswer}
              />
            </td>
          );
          break;

        case "Checkbox":
          result.push(
            <td key={`${column.type}${row.name}Column${i}`} className={incompleteClass}>
              <CheckboxRenderer
                defaultStyle
                row={row}
                column={column}
                question={question}
                disabled={disabled}
                collection={rowAnswers}
                addQuestionAnswer={addQuestionAnswer}
                setQuestionAnswer={setQuestionAnswer}
                removeQuestionAnswer={removeQuestionAnswer}
              />
            </td>
          );
          break;

        default:
          break;
      }
    }

    return result;
  }
}

TableRow.propTypes = {
  disabled: PropTypes.bool,
  question: PropTypes.object.isRequired,
  rowAnswers: PropTypes.array.isRequired,
  addQuestionAnswer: PropTypes.func.isRequired,
  setQuestionAnswer: PropTypes.func.isRequired,
  removeQuestionAnswer: PropTypes.func.isRequired,
  editRow: PropTypes.func,
  deleteRow: PropTypes.func,
  index: PropTypes.number,
  row: PropTypes.object,
  validation: PropTypes.array,
  skipFirst: PropTypes.bool,
  textExpanded: PropTypes.bool
};

export default injectIntl(TableRow);
